// services/dataProcessing.js

// ✅ PROCESS SPATIAL DATA (for Map)

import * as d3 from "d3";
export const processSpatialData = (response, selectedVariable) => {
  if (
    !response?.coords?.lat ||
    !response?.coords?.lon ||
    !response?.data?.[selectedVariable]
  ) {
    return [];
  }

  const latitudes = response.coords.lat;
  const longitudes = response.coords.lon;
  const gridData = response.data[selectedVariable]; // [lat][lon]

  const spatialData = [];

  for (let latIdx = 0; latIdx < latitudes.length; latIdx++) {
    for (let lonIdx = 0; lonIdx < longitudes.length; lonIdx++) {
      spatialData.push({
        lat: latitudes[latIdx],
        lon: longitudes[lonIdx],
        value: gridData?.[latIdx]?.[lonIdx] ?? 0,
      });
    }
  }

  return spatialData;
};

// ✅ PROCESS HEATMAP DATA (aggregate over all space, vs time — fallback if time not valid)
// services/dataProcessing.js

/**
 * Processes 3D NetCDF-like climate data into a monthly heatmap format.
 *
 * @param {object} dataObj - Object with `data` and `coords`
 * @param {string} variable - Variable to visualize (e.g., temperature, rainfall)
 * @param {[number, number]} timeRange - Optional time range filter in percent (e.g., [20, 80])
 * @returns {{ years: string[], months: string[], values: { year: string, month: string, value: number }[] }}
 */

export const processHeatmapData = (dataObj, variable) => {
  const { data, coords } = dataObj;
  const time = coords.time.map((t) => new Date(t));
  const values3D = data[variable];
  const latCount = coords.latitude.length;
  const lonCount = coords.longitude.length;

  const aggregated = {};
  time.forEach((timestamp, tIndex) => {
    const year = timestamp.getFullYear();
    const month = d3.timeFormat("%B")(timestamp);

    let sum = 0;
    let count = 0;
    for (let i = 0; i < latCount; i++) {
      for (let j = 0; j < lonCount; j++) {
        const val = values3D[tIndex]?.[i]?.[j];
        if (val !== null && !isNaN(val)) {
          sum += val;
          count++;
        }
      }
    }
    const avg = count > 0 ? sum / count : 0;
    aggregated[`${year}-${month}`] = avg;
  });

  const uniqueYears = Array.from(
    new Set(Object.keys(aggregated).map((k) => k.split("-")[0]))
  ).sort();
  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const values = [];
  uniqueYears.forEach((year) => {
    allMonths.forEach((month) => {
      const key = `${year}-${month}`;
      values.push({ year, month, value: aggregated[key] ?? 0 });
    });
  });

  return { years: uniqueYears, months: allMonths, values };
};

// ✅ PROCESS TIME SERIES DATA (select closest lat/lon → fallback dummy time series)
// services/dataProcessing.js
export function processTimeSeriesData(response, location, variable) {
  if (
    !response ||
    !response.coords ||
    !response.data ||
    !response.coords.latitude ||
    !response.coords.longitude ||
    !response.coords.time ||
    !response.data[variable]
  )
    return [];

  const lats = response.coords.latitude;
  const lons = response.coords.longitude;
  const time = response.coords.time;
  const data3D = response.data[variable]; // [time][lat][lon]

  // Find nearest grid index
  const findNearestIndex = (arr, value) =>
    arr.reduce(
      (prevIdx, currVal, idx) =>
        Math.abs(currVal - value) < Math.abs(arr[prevIdx] - value)
          ? idx
          : prevIdx,
      0
    );

  const latIdx = findNearestIndex(lats, location.lat);
  const lonIdx = findNearestIndex(lons, location.lon);

  const result = [];

  for (let t = 0; t < time.length; t++) {
    const dayOffset = time[t];
    const value = data3D[t]?.[latIdx]?.[lonIdx];

    if (typeof value === "number" && !isNaN(value)) {
      result.push({
        date: new Date(2000, 0, 1 + dayOffset), // assuming time since Jan 1, 2000
        value: value,
      });
    }
  }

  return result;
}
